"use client";

import { cn } from "./cn";

export type TabsVariant =
  | "underline"
  | "segmented"
  | "iconSwitcher";

type TabsProps<V extends string> = {
  variant: TabsVariant;
  value: V;
  onChange: (v: V) => void;
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
};

const CONTAINER: Record<TabsVariant, string> = {
  underline: "flex items-end gap-1 flex-wrap",
  segmented:
    "inline-flex p-0.5 rounded-lg bg-[#F2F4F7] border border-[#EAECF0]",
  iconSwitcher:
    "relative inline-flex items-center rounded-lg border border-[#E4E7EC] bg-white p-0.5 h-9",
};

export function Tabs<V extends string>({
  variant,
  value,
  onChange,
  children,
  className,
  ariaLabel,
}: TabsProps<V>) {
  return (
    <TabsContext.Provider value={{ variant, value, onChange: onChange as (v: string) => void }}>
      <div role="tablist" aria-label={ariaLabel} className={cn(CONTAINER[variant], className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}


import { createContext, useContext } from "react";

type Ctx = {
  variant: TabsVariant;
  value: string;
  onChange: (v: string) => void;
};

const TabsContext = createContext<Ctx | null>(null);

function useTabs(): Ctx {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("<Tab> must be inside <Tabs>");
  return ctx;
}


type TabProps = {
  value: string;
  icon?: React.ReactNode;
  count?: number;
  children: React.ReactNode;
  accent?: string;
};

export function Tab({ value, icon, count, children, accent }: TabProps) {
  const { variant, value: current, onChange } = useTabs();
  const active = value === current;

  switch (variant) {
    case "underline":
      return (
        <button
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(value)}
          className={cn(
            "relative inline-flex items-center gap-2 px-3 pt-2 pb-3 text-[13.5px] font-semibold transition",
            active
              ? "text-[#101828]"
              : "text-[#6B7280] hover:text-[#101828]",
          )}
        >
          {icon && <span className="w-3.5 h-3.5">{icon}</span>}
          {children}
          {count !== undefined && (
            <span
              className={cn(
                "text-[11px] rounded-full px-1.5 py-[1px] font-semibold tabular-nums",
                active
                  ? "bg-brand-soft text-brand-cta"
                  : "bg-[#F2F4F7] text-[#6B7280]",
              )}
            >
              {count}
            </span>
          )}
          {active && (
            <span
              className="absolute left-3 right-3 -bottom-px h-[2px] rounded-t"
              style={{ background: accent ?? "#0EB64F" }}
            />
          )}
        </button>
      );

    case "segmented":
      return (
        <button
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(value)}
          className={cn(
            "flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-semibold transition",
            active
              ? "bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.08)]"
              : "text-[#6B7280] hover:text-[#101828]",
          )}
        >
          {icon && <span className="w-3.5 h-3.5">{icon}</span>}
          {children}
        </button>
      );

    case "iconSwitcher":
      return (
        <button
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(value)}
          className={cn(
            "relative z-10 w-8 h-8 rounded-md flex items-center justify-center transition",
            active ? "text-[#101828]" : "text-[#98A2B3] hover:text-[#6B7280]",
          )}
          aria-label={typeof children === "string" ? children : undefined}
        >
          {icon && <span className="w-4 h-4">{icon}</span>}
          {!icon && children}
        </button>
      );
  }
}
